import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

// Page size is large enough to load all collection rows in one request so the
// UI can group by leading title prefix (Brands / Designers / Product Families,
// …) without group blocks being split across pages. ~3,500 rows ≈ 700
// collections × 5 fields covers the live LampTwist catalog with headroom.
const DEFAULT_PAGE_SIZE = 5000;

/**
 * Hook for the Collections browse page (Phase 15, plan 15-04).
 *
 * Mirrors useThemeStrings (Phase 14) with these deltas:
 *   - Drops the resource-type and key-prefix Phase 14 filters (single resource type here, bounded 5-field set)
 *   - Adds fieldKey filter (6 options: '', title, body_html, handle, meta_title, meta_description)
 *   - Adds hasHtml tri-state filter ('', 'true', 'false')
 *   - DEFAULT_PAGE_SIZE bumped to 250 (~50 collections × 5 fields per group, cleaner pagination boundary)
 *
 * Filters are SERVER-SIDE: every filter change refetches with query params, matching Plan 03's API.
 */
export default function useCollections() {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (server-side per D-12)
  const [fieldKey, setFieldKey] = useState('');   // '' | 'title' | 'body_html' | 'handle' | 'meta_title' | 'meta_description'
  const [policy, setPolicy] = useState('all');    // 'all' | 'translate' | 'skip' | 'auto-skipped'
  const [hasHtml, setHasHtml] = useState('');     // '' | 'true' | 'false'
  const [status, setStatus] = useState('all');    // 'all' | 'missing'
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms — matches Phase 14)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (fieldKey) params.field_key = fieldKey;
      if (policy && policy !== 'all') params.policy = policy;
      if (hasHtml) params.has_html = hasHtml;
      if (status && status !== 'all') params.status = status;
      if (debouncedQ) params.q = debouncedQ;

      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/collections/', { params }),
        axios.get('/api/transly/collections/sync-status/'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, fieldKey, policy, hasHtml, status, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters change (avoid stranded pages)
  useEffect(() => {
    setPage(1);
  }, [fieldKey, policy, hasHtml, status, debouncedQ]);

  const updatePolicy = useCallback(async (collectionId, newPolicy) => {
    // Optimistic update — flip the row first, then PATCH; rollback on error.
    const prev = rows;
    setRows((curr) =>
      curr.map((r) =>
        r.id === collectionId
          ? {
              ...r,
              translation_policy: newPolicy,
              auto_skip_reason: newPolicy === null ? r.auto_skip_reason : null,
            }
          : r,
      ),
    );
    try {
      await axios.patch(`/api/transly/collections/${collectionId}/policy/`, {
        policy: newPolicy,
      });
      // For policy=null (reset to auto), the server re-classified — refetch to learn the new auto_skip_reason.
      if (newPolicy === null) {
        await fetchData();
      }
    } catch (err) {
      // Rollback
      setRows(prev);
      throw err;
    }
  }, [rows, fetchData]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/collections/sync/');
  }, []);

  return {
    // Data
    rows,
    total,
    loading,
    error,
    // Filters
    fieldKey,
    setFieldKey,
    policy,
    setPolicy,
    hasHtml,
    setHasHtml,
    status,
    setStatus,
    q,
    setQ,
    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize,
    // Sync
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    triggerSync,
    // Actions
    updatePolicy,
    fetchData,
  };
}
